<template>
  <button
    id="deal-discovery-btn"
    class="deal-discovery-btn btn btn-sm"
    :class="buttonOptions.styles"
    :disabled="showModal"
    @click="async () => await clickHandler()">
    <stars style="scale: calc(1.25)" />
    {{ buttonOptions.text }}
    <icon-circle-x
      v-if="filtersApplied"
      class="ml-3 mr-0" />
  </button>
  <modal
    :id="'deal-discovery-modal'"
    class="deal-discovery-modal"
    :class="{ 'modal-results-view': completedWithResults }"
    :visible="showModal"
    :button1-visible="completedWithResults"
    :button2-visible="showConfiguration"
    :button3-visible="true"
    :button1-caption="capitalize($t('actions.applyFilters'))"
    :button2-caption="capitalize($t('actions.continue'))"
    :button3-caption="capitalize($t('actions.cancel'))"
    :button2-callback="async () => await onConfigurationConfirmed()"
    :button2-disabled="isConfirmedDisabled"
    :button2-class="'primary'"
    :size="'60'"
    :show-header="false"
    @confirmed="onConfirmed"
    @cancelled="resetDefaults">
    <template #modal-before-text>
      <div
        v-if="showModal"
        class="p-4 px-5">
        <!-- Header -->
        <div
          v-if="completedWithResults"
          class="d-flex justify-content-between align-items-center mb-5">
          <div class="icon-wrapper align-items-center">
            <stars class="discovery-icon" />
          </div>
          <div class="deal-discovery-kpis">
            <kpi
              class="deal-discovery-kpi"
              :label="capitalize($t('names.deal', { count: dealDiscoveryStatistics.totalTargetedDeals }))"
              :value="dealDiscoveryStatistics.totalTargetedDealsFormatted"
              :is-money="false"
              :label-style="{ 'font-size': '1.25rem' }"
              :icon-style="{ width: '2.5rem', height: '2.5rem' }"
              :value-style="{ 'font-size': '1.5rem' }"
              icon="icon-colored-deal" />
            <kpi
              class="deal-discovery-kpi"
              :label="capitalize($t('names.publisher', { count: dealDiscoveryStatistics.sellersTargetedCount }))"
              :value="dealDiscoveryStatistics.sellersTargetedCountFormatted"
              :is-money="false"
              :label-style="{ 'font-size': '1.25rem' }"
              :icon-style="{ width: '2.5rem', height: '2.5rem' }"
              :value-style="{ 'font-size': '1.5rem' }"
              icon="icon-colored-advertiser" />
            <kpi
              class="deal-discovery-kpi"
              :label="capitalize($t('names.screen', { count: dealDiscoveryStatistics.unitsTargetedByDealsCount }))"
              :value="dealDiscoveryStatistics.unitsTargetedByDealsCountFormatted"
              :is-money="false"
              :label-style="{ 'font-size': '1.25rem' }"
              :icon-style="{ width: '2.5rem', height: '2.5rem' }"
              :value-style="{ 'font-size': '1.5rem' }"
              icon="icon-colored-unit" />
          </div>
        </div>
        <!-- Summary Description -->
        <div
          v-if="hasCompleted"
          class="mt-5">
          <div v-if="hasDealsMatchingTargeting">
            <p
              class="h1 mb-2"
              style="font-size: 2.25rem">
              {{
                capitalize(
                  $t('messages.dealDiscoveryCoverage', { percent: dealDiscoveryStatistics.coverageRateFormatted }),
                )
              }}
            </p>
            <p
              class="h2 lead mt-3"
              style="font-size: 1.65rem">
              {{ capitalize(dealDiscoveryDescription) }}
            </p>
            <p
              v-if="dealDiscoveryStatistics.unitsNotTargetedCount > 0"
              style="font-size: 1.25rem"
              class="text-muted mt-4 mb-0">
              {{
                capitalize(
                  $t('messages.dealDiscoveryUnavailableUnits', {
                    count: dealDiscoveryStatistics.unitsNotTargetedCount,
                    amount: dealDiscoveryStatistics.unitsNotTargetedFormatted,
                  }),
                )
              }}
            </p>
          </div>
          <div
            v-else
            class="mt-4">
            <p class="h2 mb-2 modal-header-text">{{ capitalize($t('messages.dealsNotFound')) }}</p>
            <p class="text-muted modal-subheader-text">
              {{ capitalize(dealDiscoveryDescription) }}
            </p>
          </div>
        </div>
        <!-- Error View -->
        <div
          v-else-if="hasFailed"
          class="mt-4">
          <p class="h2 mb-2 modal-header-text">{{ capitalize($t('messages.somethingWentWrong')) }}</p>
          <p class="text-muted modal-subheader-text">
            {{ capitalize($t('messages.somethingWentWrongDescription')) }}
          </p>
        </div>
        <!-- Configuration Prompt -->
        <div
          v-else-if="showConfiguration"
          class="deal-discovery-currency-prompt d-flex flex-column mt-3">
          <template v-if="promptForCurrency">
            <p class="h3">{{ capitalize($t('messages.preferredCurrency')) }}</p>
            <p class="text-muted modal-subheader-text">
              {{ capitalizeTranslation('messages.dealDiscoveryCurrencyPrompt') }}
            </p>
            <div class="currency-multiselect d-flex mt-3">
              <multiselect
                v-model="currencyInput"
                :options="currencyOptions"
                track-by="id"
                label="name" />
            </div>
          </template>
          <div
            v-if="promptForVisibility && promptForCurrency"
            class="my-3">
            <hr />
          </div>
          <template v-if="promptForVisibility">
            <p class="h3">{{ capitalizeTranslation('names.dealVisibility') }}</p>
            <p class="text-muted modal-subheader-text">
              {{ capitalizeTranslation('messages.dealDiscoveryVisibilityPrompt') }}
            </p>
            <div class="d-flex flex-column align-items-start">
              <label
                v-if="curatePrivateDealsAllowed"
                class="control-label"
                for="input_private_deals">
                <input
                  class="mr-2"
                  id="input_private_deals"
                  v-model="visibilityInput"
                  type="checkbox"
                  name="privateDeals"
                  :value="PackageRestrictions.PRIVATE" />
                {{ capitalizeTranslation('names.private') }}
              </label>
              <label
                class="control-label"
                for="input_open_deals">
                <input
                  class="mr-2"
                  id="input_open_deals"
                  v-model="visibilityInput"
                  type="checkbox"
                  name="openDeals"
                  :value="PackageRestrictions.OPEN" />
                {{ capitalizeTranslation('names.open') }}
              </label>
              <label
                class="control-label"
                for="input_curator_exclusive_deals">
                <input
                  class="mr-2"
                  id="input_curator_exclusive_deals"
                  v-model="visibilityInput"
                  type="checkbox"
                  name="curatorExclusiveDeals"
                  :value="PackageRestrictions.CURATOR" />
                {{ capitalizeTranslation('names.curatorExclusive') }}
              </label>
            </div>
          </template>
        </div>
        <!-- Loader Section -->
        <div
          v-else
          class="discovery-loader">
          <p class="h2 text-center mb-5 modal-header-text">
            {{ dealDiscoveryLoaderMessage }}
          </p>
          <fake-loading-progress-bar
            :completion-trigger="isLoading"
            @progress-bar-completed="() => (progressBarCompleted = true)" />
        </div>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
  import { capitalize, computed, Ref, ref } from 'vue';
  import { useI18n } from 'vue-i18n';

  import { Modal } from '@/common/components/controls';
  import FakeLoadingProgressBar from '@/common/components/ui/FakeLoadingProgressBar.vue';
  import { IconCircleX } from '@/common/icons/monochrome';
  import Stars from '@/common/icons/monochrome/Stars.vue';
  import { Package } from '@/common/models';
  import DealDiscoveryStatistics from '@/common/models/package/DealDiscovery';
  import PackageRestrictions from '@/common/models/package/PackageRestrictions.enum';
  import { capitalizeTranslation } from '@/globals';
  import Kpi from '@/org-app/components/controls/KPI.vue';
  import rootStore from '@/store';
  import { mapGetters } from '@/utils/StateMapping';

  interface IDealDiscoveryButtonProps {
    deal: Package;
    selectedCurrencies: string[];
    allCurrencies: string[];
  }

  const props = defineProps<IDealDiscoveryButtonProps>();
  const emits = defineEmits(['statisticsChanged']);
  const i18n = useI18n();

  const { membershipSettings } = mapGetters();

  // Data
  const dealDiscoveryStatistics: Ref<DealDiscoveryStatistics> = ref(null);
  const filtersApplied = ref(false);
  const showModal = ref(false);
  const hasFailed = ref(false);

  // Loaders
  const isLoading = ref(false);
  const progressBarCompleted = ref(false);

  // Configuration
  const promptForVisibility = ref(false);
  const promptForCurrency = ref(false);
  const currencyInput = ref(null);
  const visibilityInput = ref([]);

  // Custom loading message
  const dealDiscoveryLoaderMessage = ref(capitalize(i18n.t('states.loading')));

  const preSelectedCurrency = computed(() => {
    return props.selectedCurrencies.length === 1 ? props.selectedCurrencies[0] : null;
  });

  const selectedCurrency = computed(() => {
    if (currencyInput.value) {
      return currencyInput.value.id;
    } else {
      return preSelectedCurrency.value;
    }
  });

  const currencyOptions = computed(() => {
    const data = props.selectedCurrencies.length > 0 ? props.selectedCurrencies : props.allCurrencies;
    return data.map((currency: string) => {
      return {
        id: currency,
        name: currency,
      };
    });
  });

  const hasCompleted = computed(() => {
    return progressBarCompleted.value && !promptForVisibility.value && !promptForCurrency.value;
  });

  const hasDealsMatchingTargeting = computed(() => {
    return dealDiscoveryStatistics.value?.totalTargetedDeals > 0;
  });

  const completedWithResults = computed(() => {
    return hasCompleted.value && hasDealsMatchingTargeting.value;
  });

  const buttonOptions = computed(() => {
    return {
      styles: !filtersApplied.value ? 'btn-primary' : 'btn-default-hollow dashed-borders',
      text: !filtersApplied.value
        ? capitalize(i18n.t('messages.dealDiscoveryDealSeeker'))
        : capitalize(i18n.t('messages.dealDiscoveryDisableDealSeeker')),
    };
  });

  const dealDiscoveryDescription = computed(() => {
    if (!dealDiscoveryStatistics.value || dealDiscoveryStatistics.value.totalTargetedDeals === 0) {
      return i18n.t('messages.dealDiscoveryNoDealsFound');
    }
    const sellersAmount = i18n.t('messages.publisherCount', {
      amount: dealDiscoveryStatistics.value.sellersTargetedCountFormatted,
      count: dealDiscoveryStatistics.value.sellersTargetedCount,
    });
    const unitsAmount = i18n.t('messages.unitCount', {
      amount: dealDiscoveryStatistics.value.unitsTargetedByDealsCountFormatted,
      count: dealDiscoveryStatistics.value.unitsTargetedByDealsCount,
    });
    const dealsAmount = i18n.t('messages.dealCount', {
      amount: dealDiscoveryStatistics.value.totalTargetedDealsFormatted,
      count: dealDiscoveryStatistics.value.totalTargetedDeals,
    });
    return i18n.t('messages.dealDiscoverySummary', {
      publishers: sellersAmount,
      units: unitsAmount,
      deals: dealsAmount,
    });
  });

  const showConfiguration = computed(() => {
    return promptForVisibility.value || promptForCurrency.value;
  });

  const curatePrivateDealsAllowed = computed(() => {
    return membershipSettings.value?.curatePrivateDealsAllowed;
  });

  const isConfirmedDisabled = computed(() => {
    const isCurrencySelectionInvalid = promptForCurrency.value && !currencyInput.value;
    const isVisibilitySelectionInvalid = promptForVisibility.value && !visibilityInput.value.length;
    return isCurrencySelectionInvalid || isVisibilitySelectionInvalid;
  });

  async function onConfigurationConfirmed() {
    promptForVisibility.value = false;
    promptForCurrency.value = false;
    await getPackagesMatchingTargeting();
    visibilityInput.value = [];
  }

  function onConfirmed() {
    filtersApplied.value = true;
    emits('statisticsChanged', {
      dealDiscoveryStatistics: dealDiscoveryStatistics.value,
      currencyUsed: selectedCurrency.value,
    });
    showModal.value = false;
  }

  async function getPackagesMatchingTargeting() {
    try {
      isLoading.value = true;
      dealDiscoveryStatistics.value = await rootStore.dispatch('orgApp/getPackagesMatchingTargeting', {
        payload: {
          ...props.deal.dealDiscoveryParams,
          currency: selectedCurrency.value,
          allowed_buyer_access: visibilityInput.value,
        },
      });
    } catch (error) {
      hasFailed.value = true;
    } finally {
      isLoading.value = false;
    }
  }

  async function clickHandler() {
    if (!dealDiscoveryStatistics.value) {
      dealDiscoveryLoaderMessage.value = i18n.t(
        'messages.dealDiscoveryLoadingMessages',
        Math.floor(Math.random() * 2) + 1,
      );
      await initializeDealDiscoveryStatistics();
    } else {
      clearDealDiscoveryStatistics();
    }
  }

  async function initializeDealDiscoveryStatistics() {
    promptForCurrency.value = !preSelectedCurrency.value;
    promptForVisibility.value = true;
    showModal.value = true;
  }

  function clearDealDiscoveryStatistics() {
    resetDefaults();
    emits('statisticsChanged', null);
  }

  function resetDefaults() {
    dealDiscoveryStatistics.value = null;
    filtersApplied.value = false;
    progressBarCompleted.value = false;
    currencyInput.value = null;
    visibilityInput.value = [];
    hasFailed.value = false;
    showModal.value = false;
  }
</script>

<style scoped lang="scss">
  @use '@/styles/variables' as *;

  .deal-discovery-btn {
    display: flex;
    align-items: center;
    border-radius: 100px !important;
  }

  .dashed-borders {
    border: dashed 0.1rem $borders;
  }

  .discovery-results {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    column-gap: 1.5rem;
  }

  .discovery-results > div {
    background-color: darkgrey;
    padding: 0.8rem;
    display: flex;
    justify-content: center;
    flex-direction: column;
    text-align: center;
    font-size: 1.75rem;
    border-radius: 0.5rem;
  }

  .icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 6.25rem !important;
    height: 6.25rem !important;
    background: linear-gradient(135deg, #fc466b 0%, #3f5efb 100%);
    border-radius: 100%;
    box-shadow:
      0 0 10px rgba(255, 255, 255, 0.8),
      0 0 20px rgba(255, 255, 255, 0.6),
      0 0 30px rgba(255, 255, 255, 0.4);

    svg {
      stroke: white;
      transform: scale(1.5);
    }
  }

  .deal-discovery-kpis {
    display: grid;
    grid-gap: 0.75rem;
    grid-template-columns: 1fr 1.25fr 1fr;
    border: solid 1px $borders-light;
    background: $almost-white;
    border-radius: 0.8rem;
    height: 7rem;
  }

  .modal-header-text {
    font-size: 2rem !important;
  }

  .modal-subheader-text {
    font-size: 1.5rem !important;
  }

  .deal-discovery-modal {
    ::v-deep(.modal-dialog) {
      border-radius: 15px;
    }
    ::v-deep(.modal-content) {
      border-radius: 15px;
      border: 0;
    }
  }

  .modal-results-view {
    ::v-deep(.modal-content) {
      background: linear-gradient(
        170deg,
        rgb(170, 111, 208) 1%,
        rgb(168, 146, 210) 6%,
        rgb(198, 177, 228) 10%,
        rgb(255, 255, 255) 31%
      ) !important;
    }
  }

  .currency-multiselect {
    ::v-deep(.multiselect) {
      .multiselect__content-wrapper {
        position: fixed;
        width: 83.5%;
        max-height: unset;
      }
    }
  }
</style>
